import { useMemo, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";

import { useUser } from "@components/user-context";
import { useCompanyDropdownUsers } from "@hooks/use-company-dropdown-users";
import { type Navbar as NavbarQuery } from "@north-beam/nb-client-side";
import { Select } from "@shared/selects";
import { usePrefs } from "@utils/client-side-preferences";

type ImpersonateClientSelectModel = {
  id: string;
  email: string;
  isAdmin: boolean;
  companyName: string | null;
};

interface Props {
  data: NavbarQuery | undefined;
}

export const ImpersonateClientSelect = ({ data }: Props) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [isClientSelectOpen, setIsClientSelectOpen] = useState(false);

  const prefs = usePrefs();

  const location = useLocation();

  const usersList = useCompanyDropdownUsers(data?.users);

  const value = useMemo(
    () =>
      usersList?.find((d) =>
        [user.impersonatedUser?.id, user.displayUserId].includes(d.id),
      ),
    [user, usersList],
  );

  const selectOptions = usersList.map((i) => ({
    value: i,
    label: prefs.isDemoMode ? "Widgets Co" : i.companyName ?? "",
  }));

  const currentSelectedClient = useMemo(
    () =>
      usersList?.find((d) =>
        [user?.impersonatedUser?.id, user.displayUserId].includes(d.id),
      ),
    [user, usersList],
  );
  if (!isClientSelectOpen) {
    return (
      <motion.div
        className={clsx(
          "font-bold text-2xl text-v2-primary",
          !usersList.length && "cursor-default",
          usersList.length && "cursor-pointer",
        )}
        transition={{ duration: 0.4 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => {
          if (usersList.length) {
            setIsClientSelectOpen(true);
          }
        }}
      >
        {prefs.isDemoMode
          ? "Widgets Co"
          : currentSelectedClient?.companyName || user.companyName}
      </motion.div>
    );
  }

  return (
    <motion.div
      transition={{ duration: 0.4 }}
      initial={{ width: 0 }}
      animate={{ width: "12rem" }}
      exit={{ width: 0 }}
    >
      <Select<ImpersonateClientSelectModel | undefined>
        className={clsx(
          "border-transparent rounded-md text-primary",
          "focus:border-cosmic-4 hover:bg-cosmic-4",
        )}
        activeInputClassName="bg-white ring-2 ring-cosmic-1"
        isSearchable
        value={value}
        options={selectOptions}
        immediate
        autoFocus
        isDisabled={!usersList.length}
        onClose={() => setIsClientSelectOpen(false)}
        icon={<i className="fa-regular fa-search"></i>}
        onChange={(v) => {
          if (v) {
            const pathname = location.pathname.slice(1);
            const excludingUserIdPath = pathname.substring(
              pathname.indexOf("/") + 1,
            );
            navigate(
              `/${v.id}${excludingUserIdPath ? `/${excludingUserIdPath}` : ""}`,
            );
            window.location.reload();
          }
        }}
      />
    </motion.div>
  );
};
